import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";

const AuthContext = createContext(null);

// ---------------------------------------------------------------------------
// Global axios 401-refresh interceptor.
// When ANY authenticated request returns 401, we silently call /auth/refresh
// and retry the original request once. Stops the "Not authenticated" toast
// from showing up after the 15-min access-token expires while the user is
// mid-form. Concurrent 401s share a single in-flight refresh promise.
// ---------------------------------------------------------------------------
let refreshPromise = null;
axios.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const original = error.config;
    if (
      error.response?.status === 401 &&
      original &&
      !original._retried &&
      // Don't loop on the refresh / login / me endpoints themselves
      !/\/auth\/(refresh|login|register|logout)/.test(original.url || "")
    ) {
      original._retried = true;
      try {
        if (!refreshPromise) {
          refreshPromise = axios
            .post(`${API}/auth/refresh`, {}, { withCredentials: true })
            .finally(() => { refreshPromise = null; });
        }
        await refreshPromise;
        original.withCredentials = true;
        return axios(original);
      } catch {
        // refresh failed — fall through with original 401
      }
    }
    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=not auth, object=auth
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(resp.data);
    } catch {
      // Try refresh
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        const resp = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(resp.data);
      } catch {
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const resp = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(resp.data);
    return resp.data;
  };

  const register = async (email, password, name) => {
    const resp = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
    setUser(resp.data);
    return resp.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setUser(false);
  };

  const updateWatchlist = async (assetId, action) => {
    const resp = await axios.post(`${API}/watchlist`, { asset_id: assetId, action }, { withCredentials: true });
    setUser(prev => prev ? { ...prev, watchlist: resp.data.watchlist } : prev);
    return resp.data.watchlist;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateWatchlist, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
